/**
 * Type definitions for the Provider module
 */

export enum ProviderType {
  CLINICA = 'CLINICA',
  LABORATORIO = 'LABORATORIO',
  HOSPITAL = 'HOSPITAL',
  CONSULTORIO = 'CONSULTORIO',
  CENTRO_MEDICO = 'CENTRO_MEDICO',
  AMBIENTAL = 'AMBIENTAL',
  SEGURANCA = 'SEGURANCA',
  MULTIPLO = 'MULTIPLO',
}

export enum ServiceType {
  AGENDA_PROFISSIONAIS = 'Agenda dos Profissionais',
  ORDEM_CHEGADA = 'Ordem de Chegada',
  AGENDA_CLINICA = 'Hora Marcada',
}

export enum ProviderStatus {
  ATIVO = 'ATIVO',
  INATIVO = 'INATIVO',
  SUSPENSO = 'SUSPENSO',
  BLOQUEADO = 'BLOQUEADO',
  PENDENTE = 'PENDENTE',
}

/**
 * Estágio do cadastro. A clínica prospectada é a que a integração trouxe e que ainda não é um
 * prestador: não aparece em agendamento, exame nem vínculo com empresa.
 */
export enum EstagioDoPrestador {
  EMBRIONARIO = 'EMBRIONARIO',
  COMPLETO = 'COMPLETO',
}

export interface Provider {
  id: number | string;
  // Nulo enquanto for clínica prospectada: é justamente o CNPJ que a promove a prestador.
  cnpj: string | null;
  estagio?: EstagioDoPrestador;
  legalName: string;
  tradeName?: string;
  providerType: ProviderType;
  status: ProviderStatus;
  zipCode?: string;
  address?: string;
  addressNumber?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  receptionContactName?: string;
  receptionEmail?: string;
  receptionPhone?: string;
  reportContactName?: string;
  reportEmail?: string;
  reportPhone?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  bankName?: string;
  bankAgency?: string;
  bankAccount?: string;
  paymentMethod?: string;
  pix?: string;
  contractStartDate?: string | Date;
  contractEndDate?: string | Date;
  closingDay?: number | null;
  paymentDay?: number | null;
  terminationReason?: string;
  financialContactName?: string;
  financialEmail?: string;
  financialPhone?: string;
  firstContactDate?: string | Date;
  serviceType?: string;
  morningStartTime?: string;
  morningEndTime?: string;
  morningCapacity?: number;
  afternoonStartTime?: string;
  afternoonEndTime?: string;
  afternoonCapacity?: number;
  concurrentCapacity?: number;
  defaultSlotDuration?: number;
  providerNotice?: string;
  responsibleName?: string;
  responsibleCRM?: string;
  // Reajuste de valores dos exames (aplicação do percentual a ser desenvolvida posteriormente)
  priceAdjustmentDate?: string | Date;
  priceAdjustmentPercentage?: number;
  documents?: ProviderDocument[];
  weeklySchedules?: ProviderWeeklySchedule[];
  createdAt: string | Date;
  updatedAt: string | Date;
}

// Provider Document types
export enum ProviderDocumentType {
  ANVISA_LICENSE = 'ANVISA_LICENSE',
  LO = 'LO',
  AF = 'AF',
  AVCB = 'AVCB',
  IBAMA = 'IBAMA',
  CTF = 'CTF',
  CETESB = 'CETESB',
}

export const PROVIDER_DOCUMENT_TYPE_LABELS: Record<ProviderDocumentType, string> = {
  [ProviderDocumentType.ANVISA_LICENSE]: 'Licença ANVISA',
  [ProviderDocumentType.LO]: 'Licença de Operação (LO)',
  [ProviderDocumentType.AF]: 'Alvará de Funcionamento (AF)',
  [ProviderDocumentType.AVCB]: 'Auto de Vistoria do Corpo de Bombeiros (AVCB)',
  [ProviderDocumentType.IBAMA]: 'IBAMA',
  [ProviderDocumentType.CTF]: 'Cadastro Técnico Federal (CTF)',
  [ProviderDocumentType.CETESB]: 'CETESB',
};

export interface ProviderDocument {
  id?: number;
  providerId: number;
  documentType: ProviderDocumentType;
  documentNumber?: string;
  validityDate?: string;
  fileName?: string;
  originalName?: string;
  fileSize?: number;
  mimeType?: string;
}

export interface ProviderWeeklySchedule {
  id?: number;
  providerId: number;
  weekDay: number; // 0=Sunday, 6=Saturday
  startTime: string; // HH:MM
  endTime: string; // HH:MM
  capacity?: number | null;
  isActive: boolean;
}

export interface ProviderWeeklyConfig {
  defaultSlotDuration: number;
  concurrentCapacity: number | null;
}

export interface ProviderFilters {
  search?: string;
  status?: string; // Can be comma-separated
  providerType?: string; // Can be comma-separated
  state?: string; // Can be comma-separated
  city?: string;
  // Troca a listagem de prestadores cadastrados pelo recorte das clínicas prospectadas.
  apenasEmbrionarias?: boolean;
}

export interface ProviderStats {
  total: number;
  embrionarias: number;
}

export interface PaginatedProvidersResponse {
  message: string;
  data: Provider[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}